import Briefcase from '../assets/images/briefcase.png';
import RexlabsLogo from '../assets/images/rexlabs.svg';
import SerenadeLogo from '../assets/images/Serenade_500x500px.jpeg';
import VeChainLogo from '../assets/images/vechain.png';

export interface ProjectMetric {
  value: string;
  label: string;
}

export interface ProjectChunk {
  id: string;
  category: string;
  title: string;
  tagline: string;
  summary: string;
  contributions: string[];
  outcome: string;
  metrics?: ProjectMetric[];
  stack: string[];
  placeholder?: boolean;
}

export interface CompanyProject {
  id: string;
  name: string;
  role: string;
  period: string;
  logo: string;
  logoAlt: string;
  projects: ProjectChunk[];
}

const companyProjects: CompanyProject[] = [
  {
    id: 'uphold',
    name: 'Uphold',
    role: 'Senior Backend Engineer',
    period: '2026–Present',
    logo: Briefcase,
    logoAlt: 'Briefcase',
    projects: [
      {
        id: 'open-banking',
        category: 'Banking infrastructure',
        title: 'Open Banking Initiatives',
        tagline: 'Banking rails, connected.',
        summary:
          'Backend delivery connecting internal money-movement systems with external Open Banking providers.',
        contributions: [
          'Led backend delivery across internal systems and provider integrations.',
          'Worked with product and engineering teams to clarify requirements, technical boundaries and delivery trade-offs.',
          'Ramped up quickly across unfamiliar services and provider APIs while moving the initiative forward.',
        ],
        outcome:
          'Established momentum on an active Open Banking initiative while building context across the product, services and external provider landscape.',
        stack: ['TypeScript', 'Go', 'AWS', 'Kubernetes'],
      },
      {
        id: 'banking-integrations',
        category: 'Reliability & delivery',
        title: 'Banking Integration Improvements',
        tagline: 'Fix the flow. Ship the feature.',
        summary:
          'Hands-on delivery across existing banking integrations, balancing production fixes with new capability work.',
        contributions: [
          'Investigated integration issues across service and provider boundaries.',
          'Collaborated with other engineering teams to deliver fixes and new features.',
          'Communicated constraints and trade-offs clearly while coordinating changes across team ownership boundaries.',
        ],
        outcome:
          'Delivered fixes and feature work across production banking systems while coordinating changes across service, provider and team boundaries.',
        stack: ['TypeScript', 'Go', 'APIs', 'Banking integrations'],
      },
    ],
  },
  {
    id: 'vechain',
    name: 'VeChain',
    role: 'Senior Software / Blockchain Developer',
    period: '2025–2026',
    logo: VeChainLogo,
    logoAlt: 'VeChain logo',
    projects: [
      {
        id: 'governance-proposal',
        category: 'Product & governance',
        title: 'Governance Proposal & Product Definition',
        tagline: 'From community signal to shipped governance.',
        summary:
          'A product and governance initiative addressing recurring participation friction in VeBetterDAO allocation voting.',
        contributions: [
          'Researched the participation problem and authored the public proposal.',
          'Worked through community discussion, formal endorsement and DAO voting.',
          'Translated the approved proposal into a production implementation plan.',
        ],
        outcome:
          'The proposal passed with 18,892 participants and 84% quorum before being adopted into weekly DAO operations.',
        metrics: [
          { value: '18,892', label: 'voting participants' },
          { value: '84%', label: 'governance quorum' },
        ],
        stack: ['Product discovery', 'Technical design', 'VeBetterDAO'],
      },
      {
        id: 'auto-voting-relayers',
        category: 'Protocol infrastructure',
        title: 'Auto-Voting & Relayer Marketplace',
        tagline: 'Governance that keeps moving.',
        summary:
          'Production infrastructure that lets users delegate recurring votes and reward claims to economically incentivised relayers.',
        contributions: [
          'Designed and implemented the voting, voter-reward and relayer-reward smart contracts.',
          'Defined contract interfaces for frontend and external service integration.',
          'Built the user-facing allocation flow and shipped the system to VeChainThor mainnet.',
        ],
        outcome:
          'Adoption reached 1,100+ users within two months, with relayers executing hundreds of automated votes and reward claims.',
        metrics: [
          { value: '1,100+', label: 'users in two months' },
          { value: 'Mainnet', label: 'production deployment' },
        ],
        stack: ['TypeScript', 'Solidity', 'React', 'Hardhat', 'VeChainThor'],
      },
      {
        id: 'bot-appeals',
        category: 'Trust & safety',
        title: 'Bot Signalling & Appeal Flow',
        tagline: 'Make enforcement explain itself.',
        summary:
          'An auditable enforcement and appeal workflow for a token-reward programme serving more than 29,000 weekly participants.',
        contributions: [
          'Added mandatory on-chain reason codes to bot-account signals.',
          'Implemented appeal reset functionality for successfully reviewed users.',
          'Built the production appeal and verification frontend.',
        ],
        outcome:
          'The system recorded reasons across 100,000+ flagged accounts and supported the reinstatement of more than 400 legitimate users.',
        metrics: [
          { value: '100k+', label: 'signals with reasons' },
          { value: '400+', label: 'users reinstated' },
        ],
        stack: ['TypeScript', 'Solidity', 'React', 'VeChainThor'],
      },
    ],
  },
  {
    id: 'serenade',
    name: 'Serenade',
    role: 'Software / Blockchain Developer',
    period: '2022–2024',
    logo: SerenadeLogo,
    logoAlt: 'Serenade logo',
    projects: [
      {
        id: 'digital-pressing-contracts',
        category: 'Product infrastructure',
        title: 'Digital Pressing Contract Framework',
        tagline: 'One format. Hundreds of releases.',
        summary:
          'A reusable smart-contract format for standalone, chart-eligible digital music releases.',
        contributions: [
          'Designed standalone ERC-721 contracts for individual album releases.',
          'Implemented EIP-2981 royalties, IPFS metadata and contract-level marketplace compatibility.',
          'Created a repeatable deployment architecture instead of rebuilding contracts for every release.',
        ],
        outcome:
          'The framework powered releases for more than 200 artists and label partners and became recognised by UK and Australian chart bodies.',
        metrics: [
          { value: '200+', label: 'artists and partners' },
          { value: 'UK + AU', label: 'chart recognition' },
        ],
        stack: ['Solidity', 'ERC-721', 'EIP-2981', 'Polygon', 'IPFS'],
      },
      {
        id: 'minting-payments',
        category: 'Commerce & payments',
        title: 'Minting, Payments & Product Integration',
        tagline: 'From mint to marketplace.',
        summary:
          'The backend and product infrastructure connecting Digital Pressing contracts to Serenade’s consumer marketplace.',
        contributions: [
          'Built backend minting services and contributed to the marketplace payment system.',
          'Connected Polygon contracts with off-chain product and ownership flows.',
          'Coordinated with product and design to ship releases as complete customer experiences.',
        ],
        outcome:
          'The infrastructure supported limited releases for global artists including Muse and Noel Gallagher, with royalties enforced automatically on secondary sales.',
        metrics: [
          { value: 'Polygon', label: 'production network' },
          { value: 'Automatic', label: 'artist royalties' },
        ],
        stack: ['TypeScript', 'Go', 'Next.js', 'NestJS', 'Polygon'],
      },
    ],
  },
  {
    id: 'rex-software',
    name: 'Rex Software',
    role: 'Software Developer',
    period: '2018–2021',
    logo: RexlabsLogo,
    logoAlt: 'Rex Software logo',
    projects: [
      {
        id: 'agency-onboarding',
        category: 'Customer onboarding',
        title: 'Agency Onboarding & Data Migration',
        tagline: 'A million properties, moved.',
        summary:
          'Onboarding software and migration workflows that helped real estate agencies move their teams and property data into Rex.',
        contributions: [
          'Built software to guide agency onboarding and support repeatable data imports.',
          'Ran end-to-end migrations covering the mapping, validation and import of more than one million property records.',
          'Worked directly with customers, customer success, sales, design, engineering and product to resolve data and workflow issues.',
        ],
        outcome:
          'Onboarded more than 100 real estate agencies and helped support Rex’s expansion from Australia into the UK.',
        metrics: [
          { value: '100+', label: 'agencies onboarded' },
          { value: '1M+', label: 'properties migrated' },
        ],
        stack: ['React', 'TypeScript', 'Laravel', 'PHP', 'Data migrations'],
      },
      {
        id: 'core-crm',
        category: 'Core product',
        title: 'Real Estate CRM',
        tagline: 'Built where agents worked.',
        summary:
          'Customer-facing work across the core CRM used by real estate agencies to manage properties, contacts and everyday operations.',
        contributions: [
          'Built and maintained full-stack product features across React interfaces and Laravel/PHP services.',
          'Translated customer feedback and business requirements into clear, shippable improvements.',
          'Collaborated across customer-facing and product teams from early discussion through delivery and iteration.',
        ],
        outcome:
          'Improved the product agencies relied on day to day while adapting workflows for customers in both Australia and the UK.',
        stack: ['React', 'TypeScript', 'Laravel', 'PHP'],
      },
    ],
  },
];

export default companyProjects;
